import { useRef, useState } from 'react'
import useEnterStagger from './hooks/useEnterStagger.js'
import { CaretUp, CaretDown } from '@phosphor-icons/react'

// columns: [{ key, header, priority?=2, render?(row), align?, sortable? }]
// priority: 1 = card title · 2 = shown on card · 3 = behind card expand.
// Collapse to cards is pure CSS (@container ≤640px on .cn-table-wrap).
// sort/onSort: controlled sort — { key, dir: 'asc'|'desc' } | null, onSort(key).
// The kit only renders the sort affordance; the caller owns the comparator
// and passes pre-sorted rows (kit never sorts rows itself).
export default function DataTable({ columns, rows, rowKey, onRowClick, empty, sort, onSort }) {
  const [expanded, setExpanded] = useState(() => new Set())
  const bodyRef = useRef(null)
  useEnterStagger(bodyRef, { dep: rows.length, selector: ':scope > tr' })
  const hasMinor = columns.some((c) => (c.priority || 2) === 3)
  const toggle = (k) => setExpanded((prev) => {
    const next = new Set(prev)
    if (next.has(k)) next.delete(k)
    else next.add(k)
    return next
  })
  if (!rows.length) return <div className="cn-table-wrap"><div className="cn-table-empty">{empty || 'Nothing here yet.'}</div></div>
  return (
    <div className="cn-table-wrap">
      <table className="cn-table">
        <thead>
          <tr>
            {columns.map((c) => {
              const sortable = onSort && c.sortable
              const dir = sort && sort.key === c.key ? sort.dir : 'none'
              return (
                <th key={c.key} data-align={c.align || 'left'}>
                  {sortable ? (
                    <button type="button" className="cn-table-sort" data-sort={dir}
                      onClick={() => onSort(c.key)}>
                      {c.header}
                      <span className="cn-table-sort-arrow" aria-hidden="true">
                        {dir === 'asc' ? <CaretUp size={12} weight="bold" className="ph-caret-up" /> : dir === 'desc' ? <CaretDown size={12} weight="bold" className="ph-caret-down" /> : null}
                      </span>
                    </button>
                  ) : c.header}
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody ref={bodyRef}>
          {rows.map((row) => {
            const k = rowKey(row)
            return (
              <tr key={k} data-clickable={onRowClick ? 'true' : 'false'} data-expanded={expanded.has(k) ? 'true' : 'false'}
                onClick={onRowClick ? () => onRowClick(row) : undefined}>
                {columns.map((c) => (
                  <td key={c.key} data-priority={String(c.priority || 2)} data-label={c.header} data-align={c.align || 'left'}>
                    {c.render ? c.render(row) : row[c.key]}
                  </td>
                ))}
                {hasMinor && (
                  <td data-expand-cell="true">
                    <button className="cn-table-expand" onClick={(e) => { e.stopPropagation(); toggle(k) }}>
                      {expanded.has(k) ? 'Less −' : 'More +'}
                    </button>
                  </td>
                )}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
